import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { AuthComponent } from '../../v2/auth-v2/auth-v2.component';
import { CP_ICONS } from '../shared/icons';
import { BTN_PRIMARY, BTN_SECONDARY, FIELD } from '../shared/ui';

/**
 * Notices other pages may hand to /auth via ?message=. Only these exact texts
 * are shown, so a crafted link can't put arbitrary wording on the login page.
 */
export const AUTH_NOTICES: readonly string[] = [
  // lender-signup after the account is created
  'Account created successfully! Please sign in with your new credentials.',
];

export function authNotice(message: string | null | undefined): string | null {
  return message && AUTH_NOTICES.includes(message) ? message : null;
}

interface Requirement {
  key: keyof AuthComponent['passwordValidation'];
  label: string;
}

/**
 * /auth, v3. Reuses all v2 auth behaviour (email sign-in, account creation,
 * Google, remember me, error mapping, returnUrl, ?mode=signup) with a
 * Linear-style layout: no chrome, one narrow centered column.
 */
@Component({
  selector: 'app-auth-page-v3',
  standalone: true,
  imports: [FormsModule, RouterLink, NgIcon],
  viewProviders: [provideIcons(CP_ICONS)],
  templateUrl: './auth-page.component.html',
})
export class AuthPageV3Component extends AuthComponent {
  private readonly nav = inject(Router);
  private readonly title = inject(Title);
  private readonly activatedRoute = inject(ActivatedRoute);

  /** Success notice from the previous page (e.g. lender signup), if it is a known one. */
  readonly notice = authNotice(this.activatedRoute.snapshot.queryParamMap.get('message'));

  /** Where the close (X) button goes: the in-app page the visitor came from, else home. */
  readonly exitUrl = this.resolveExitUrl();

  readonly field = FIELD;
  readonly btnPrimary = BTN_PRIMARY;
  readonly btnSecondary = BTN_SECONDARY;

  // Labels unchanged from v2.
  readonly requirements: readonly Requirement[] = [
    { key: 'minLength', label: 'At least 8 characters' },
    { key: 'hasUppercase', label: 'One uppercase letter (A-Z)' },
    { key: 'hasLowercase', label: 'One lowercase letter (a-z)' },
    { key: 'hasNumber', label: 'One number (0-9)' },
    { key: 'hasSpecialChar', label: 'One special character (!@#$%^&*)' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.syncTitle();
  }

  /** Keeps the URL and tab title in sync with the view so a sign-up link can be shared or reloaded. */
  override toggleView(): void {
    super.toggleView();
    this.syncTitle();
    this.nav.navigate([], {
      queryParams: { mode: this.isSignIn ? null : 'signup' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** navigateByUrl keeps any query/fragment of the previous page intact (routerLink would encode them). */
  exit(event: MouseEvent): void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    void this.nav.navigateByUrl(this.exitUrl);
  }

  private resolveExitUrl(): string {
    // Read during construction, while the navigation to /auth is still in flight.
    const previous = (this.nav.currentNavigation() ?? this.nav.lastSuccessfulNavigation())?.previousNavigation;
    const url = previous ? this.nav.serializeUrl(previous.finalUrl ?? previous.extractedUrl) : '/';
    return url.startsWith('/auth') ? '/' : url;
  }

  private syncTitle(): void {
    this.title.setTitle(this.isSignIn ? 'Log In - creditpath.io' : 'Sign Up - creditpath.io');
  }
}
